import React, { useState } from 'react';
import { Button, CircularProgress, MenuItem, Select, Autocomplete, TextField } from '@mui/material';
import { toast } from 'react-toastify';
import { collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../../../sp-firebase/firebase';
import { Account } from '../../../sp-types/accountTypes';
import { useMaterialsStore } from '../../../components/Stores/MaterialsStore';
import { triggerExposureCalc } from '../../../hooks/useExposure';

interface ClearDataPopupProps {
  accounts: Account[];
}

export const ClearDataPopup: React.FC<ClearDataPopupProps> = ({ accounts }) => {
  const { materialsMap } = useMaterialsStore();
  const [clearAccountId, setClearAccountId] = useState<string>('');
  const [clearMaterialId, setClearMaterialId] = useState<string>('');
  const [clearProgress, setClearProgress] = useState<{ message: string; success: boolean }[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  // Get materials for selected account
  const selectedAccount = accounts.find((acc) => acc.id === clearAccountId);
  const accountMaterialIds = selectedAccount?.materialsToAccount?.map((m) => m.materialId) || [];

  const handleClearFutureOrders = async () => {
    if (!clearAccountId) {
      toast.error('Please select an account');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete all Future Orders for account ${clearAccountId}?`,
    );
    if (!confirmed) return;

    setIsDeleting(true);
    setClearProgress([]);

    try {
      setClearProgress((prev) => [...prev, { message: 'Fetching future orders...', success: true }]);

      const futureOrdersRef = collection(db, 'future-orders');
      const q = query(futureOrdersRef, where('accountId', '==', clearAccountId));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setClearProgress((prev) => [...prev, { message: 'No future orders found', success: true }]);
        setIsDeleting(false);
        return;
      }

      setClearProgress((prev) => [
        ...prev,
        { message: `Found ${querySnapshot.size} future orders to delete`, success: true },
      ]);

      let successCount = 0;
      let errorCount = 0;
      const totalCount = querySnapshot.size;

      for (let i = 0; i < querySnapshot.docs.length; i++) {
        const docSnapshot = querySnapshot.docs[i];
        try {
          await deleteDoc(docSnapshot.ref);
          successCount++;
          setClearProgress((prev) => [
            ...prev,
            { message: `✓ (${i + 1}/${totalCount}) Deleted future order ${docSnapshot.id}`, success: true },
          ]);
        } catch (error) {
          errorCount++;
          setClearProgress((prev) => [
            ...prev,
            { message: `✗ (${i + 1}/${totalCount}) Failed to delete ${docSnapshot.id}: ${error}`, success: false },
          ]);
        }
      }

      // Also delete from future-orders-exposure collection
      setClearProgress((prev) => [...prev, { message: 'Deleting exposure records...', success: true }]);
      const exposureRef = collection(db, 'future-orders-exposure');
      const exposureQuery = query(exposureRef, where('accountId', '==', clearAccountId));
      const exposureSnapshot = await getDocs(exposureQuery);

      let exposureDeleteCount = 0;
      for (const docSnapshot of exposureSnapshot.docs) {
        try {
          await deleteDoc(docSnapshot.ref);
          exposureDeleteCount++;
        } catch (error) {
          console.error('Failed to delete exposure record:', error);
        }
      }
      setClearProgress((prev) => [
        ...prev,
        { message: `✓ Deleted ${exposureDeleteCount} exposure records`, success: true },
      ]);

      setClearProgress((prev) => [
        ...prev,
        {
          message: `\nCompleted: ${successCount} deleted, ${errorCount} failed`,
          success: errorCount === 0,
        },
      ]);

      if (successCount > 0) {
        toast.success(`Deleted ${successCount} future orders`);
        // Trigger exposure recalculation
        try {
          setClearProgress((prev) => [...prev, { message: 'Triggering exposure recalculation...', success: true }]);
          await triggerExposureCalc({
            accountId: clearAccountId,
            materialIds: [clearMaterialId],
          });
          setClearProgress((prev) => [...prev, { message: '✓ Exposure recalculation triggered', success: true }]);
        } catch (error) {
          setClearProgress((prev) => [
            ...prev,
            { message: `✗ Exposure recalculation failed: ${error}`, success: false },
          ]);
        }
      }
    } catch (error) {
      setClearProgress((prev) => [...prev, { message: `Error: ${error}`, success: false }]);
      toast.error('Failed to delete future orders');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClearInventoryReports = async () => {
    if (!clearAccountId || !clearMaterialId) {
      toast.error('Please select both account and material');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete all Inventory Reports for account ${clearAccountId} and material ${clearMaterialId}?`,
    );
    if (!confirmed) return;

    setIsDeleting(true);
    setClearProgress([]);

    try {
      setClearProgress((prev) => [...prev, { message: 'Fetching inventory reports...', success: true }]);

      const inventoryReportsRef = collection(db, 'InventoryReports');
      const q = query(
        inventoryReportsRef,
        where('accountId', '==', clearAccountId),
        where('materialId', '==', clearMaterialId),
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setClearProgress((prev) => [...prev, { message: 'No inventory reports found', success: true }]);
        setIsDeleting(false);
        return;
      }

      setClearProgress((prev) => [
        ...prev,
        { message: `Found ${querySnapshot.size} inventory reports to delete`, success: true },
      ]);

      let successCount = 0;
      let errorCount = 0;
      const totalCount = querySnapshot.size;

      for (let i = 0; i < querySnapshot.docs.length; i++) {
        const docSnapshot = querySnapshot.docs[i];
        try {
          await deleteDoc(docSnapshot.ref);
          successCount++;
          setClearProgress((prev) => [
            ...prev,
            { message: `✓ (${i + 1}/${totalCount}) Deleted inventory report ${docSnapshot.id}`, success: true },
          ]);
        } catch (error) {
          errorCount++;
          setClearProgress((prev) => [
            ...prev,
            { message: `✗ (${i + 1}/${totalCount}) Failed to delete ${docSnapshot.id}: ${error}`, success: false },
          ]);
        }
      }

      setClearProgress((prev) => [
        ...prev,
        {
          message: `\nCompleted: ${successCount} deleted, ${errorCount} failed`,
          success: errorCount === 0,
        },
      ]);

      if (successCount > 0) {
        toast.success(`Deleted ${successCount} inventory reports`);
        // Trigger exposure recalculation
        try {
          setClearProgress((prev) => [...prev, { message: 'Triggering exposure recalculation...', success: true }]);
          await triggerExposureCalc({
            accountId: clearAccountId,
            materialIds: [clearMaterialId],
          });
          setClearProgress((prev) => [...prev, { message: '✓ Exposure recalculation triggered', success: true }]);
        } catch (error) {
          setClearProgress((prev) => [
            ...prev,
            { message: `✗ Exposure recalculation failed: ${error}`, success: false },
          ]);
        }
      }
    } catch (error) {
      setClearProgress((prev) => [...prev, { message: `Error: ${error}`, success: false }]);
      toast.error('Failed to delete inventory reports');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClearFinancialPositions = async () => {
    if (!clearAccountId || !clearMaterialId) {
      toast.error('Please select both account and material');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete all Financial Positions for account ${clearAccountId} and material ${clearMaterialId}?`,
    );
    if (!confirmed) return;

    setIsDeleting(true);
    setClearProgress([]);

    try {
      setClearProgress((prev) => [...prev, { message: 'Fetching financial positions...', success: true }]);

      const financialPositionsRef = collection(db, 'financial-positions');
      const q = query(
        financialPositionsRef,
        where('accountId', '==', clearAccountId),
        where('materialId', '==', clearMaterialId),
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setClearProgress((prev) => [...prev, { message: 'No financial positions found', success: true }]);
        setIsDeleting(false);
        return;
      }

      setClearProgress((prev) => [
        ...prev,
        { message: `Found ${querySnapshot.size} financial positions to delete`, success: true },
      ]);

      let successCount = 0;
      let errorCount = 0;
      const totalCount = querySnapshot.size;

      for (let i = 0; i < querySnapshot.docs.length; i++) {
        const docSnapshot = querySnapshot.docs[i];
        try {
          await deleteDoc(docSnapshot.ref);
          successCount++;
          setClearProgress((prev) => [
            ...prev,
            { message: `✓ (${i + 1}/${totalCount}) Deleted financial position ${docSnapshot.id}`, success: true },
          ]);
        } catch (error) {
          errorCount++;
          setClearProgress((prev) => [
            ...prev,
            { message: `✗ (${i + 1}/${totalCount}) Failed to delete ${docSnapshot.id}: ${error}`, success: false },
          ]);
        }
      }

      setClearProgress((prev) => [
        ...prev,
        {
          message: `\nCompleted: ${successCount} deleted, ${errorCount} failed`,
          success: errorCount === 0,
        },
      ]);

      if (successCount > 0) {
        toast.success(`Deleted ${successCount} financial positions`);
        // Trigger exposure recalculation
        try {
          setClearProgress((prev) => [...prev, { message: 'Triggering exposure recalculation...', success: true }]);
          await triggerExposureCalc({
            accountId: clearAccountId,
            materialIds: [clearMaterialId],
          });
          setClearProgress((prev) => [...prev, { message: '✓ Exposure recalculation triggered', success: true }]);
        } catch (error) {
          setClearProgress((prev) => [
            ...prev,
            { message: `✗ Exposure recalculation failed: ${error}`, success: false },
          ]);
        }
      }
    } catch (error) {
      setClearProgress((prev) => [...prev, { message: `Error: ${error}`, success: false }]);
      toast.error('Failed to delete financial positions');
    } finally {
      setIsDeleting(false);
    }
  };

  const futureOrdersButtonEnabled = clearAccountId && !isDeleting;
  const buttonsEnabled = clearAccountId && clearMaterialId && !isDeleting;

  return (
    <div className="bg-white rounded-lg p-6 max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Clear Data by Account & Material</h2>

      <div className="flex flex-col gap-6 mb-6">
        {/* Account Selector */}
        <div className="flex flex-col gap-2">
          <label className="font-semibold">Select Account *</label>
          <Select
            value={clearAccountId}
            onChange={(e) => {
              setClearAccountId(e.target.value);
              setClearMaterialId(''); // Reset material when account changes
            }}
            displayEmpty
            fullWidth
          >
            <MenuItem value="" disabled>
              <em>Select Account</em>
            </MenuItem>
            {accounts.map((acc) => (
              <MenuItem key={acc.id} value={acc.id}>
                {acc.companyDetails?.name || acc.id}
              </MenuItem>
            ))}
          </Select>
        </div>

        {/* Material Selector */}
        <div className="flex flex-col gap-2">
          <label className="font-semibold">Select Material *</label>
          <Autocomplete
            options={accountMaterialIds.map((matId) => ({
              label: `${materialsMap[matId]?.name || matId} (${matId})`,
              value: matId,
            }))}
            value={
              clearMaterialId
                ? {
                    label: `${materialsMap[clearMaterialId]?.name || clearMaterialId} (${clearMaterialId})`,
                    value: clearMaterialId,
                  }
                : null
            }
            onChange={(_, newValue) => {
              setClearMaterialId(newValue?.value || '');
            }}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => <TextField {...params} placeholder="Select Material" />}
            disabled={!clearAccountId}
            fullWidth
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-3 mb-6">
        <Button
          variant="contained"
          color="error"
          onClick={handleClearFutureOrders}
          disabled={!futureOrdersButtonEnabled}
          fullWidth
        >
          Clear Future Orders
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleClearInventoryReports}
          disabled={!buttonsEnabled}
          fullWidth
        >
          Clear Inventory Reports
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleClearFinancialPositions}
          disabled={!buttonsEnabled}
          fullWidth
        >
          Clear Financial Positions
        </Button>
      </div>

      {/* Progress Display */}
      {clearProgress.length > 0 && (
        <>
          <div className="bg-gray-50 rounded p-4 max-h-96 overflow-y-auto">
            <h3 className="font-semibold mb-2">Progress:</h3>
            {isDeleting && (
              <div className="flex items-center gap-2 mb-2">
                <CircularProgress size={20} />
                <span>Deleting...</span>
              </div>
            )}
            <div className="flex flex-col gap-1">
              {clearProgress.map((item, idx) => (
                <div
                  key={idx}
                  className={`text-sm ${item.success ? 'text-green-700' : 'text-red-700'}`}
                  style={{ whiteSpace: 'pre-line' }}
                >
                  {item.message}
                </div>
              ))}
            </div>
          </div>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setClearProgress([])}
            disabled={isDeleting}
            className="mt-2"
          >
            Clear Log
          </Button>
        </>
      )}
    </div>
  );
};
